import { Refresh } from "@mui/icons-material";
import { IconButton, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material";
import TeamItem from "./TeamItem";
import { selectWorldCupResult, simWorldCupResultAgainAsync } from "./WorldCupResultSlice";
import { useDispatch, useSelector } from "react-redux";

export default function MatchTable(props) {
    const { stage, groupIndex, matchIndex } = { ...props };
    const result = useSelector(selectWorldCupResult);
    const dispatch = useDispatch();
    const match = getMatch(result, stage, groupIndex, matchIndex);
    const team1Sx = { border: 0 };
    const team2Sx = { border: 0 };
    const goal1 = match?.goal1;
    const goal2 = match?.goal2;
    const psoWinner = match?.psoWinner;
    if (stage !== "G" && goal1 !== undefined && goal2 !== undefined) {
        const winnerSx = { color: stage === "F" ? "gold" : "text.primary", fontWeight: "bold" };
        const loserSx = { color: "text.secondary" };
        if (goal1 > goal2 || psoWinner === 1) {
            Object.assign(team1Sx, winnerSx);
            Object.assign(team2Sx, loserSx);
        } else if (goal2 > goal1 || psoWinner === 2) {
            Object.assign(team2Sx, winnerSx);
            Object.assign(team1Sx, loserSx);
        }
    }
    let team1, team2, result1, result2;
    if ((goal1 === undefined || goal2 === undefined) && result.loading) {
        result1 = <Skeleton animation={false} />;
        result2 = <Skeleton animation={false} />;
    } else {
        result1 = goal1;
        if (psoWinner === 1) {
            result1 += "p";
        }
        result2 = goal2;
        if (psoWinner === 2) {
            result2 += "p";
        }
    }
    if (match?.team1) {
        team1 = <TeamItem code={match.team1} />;
    } else if (result.loading) {
        team1 = <Skeleton animation={false} />;
    }
    if (match?.team2) {
        team2 = <TeamItem code={match.team2} />;
    } else if (result.loading) {
        team2 = <Skeleton animation={false} />;
    }
    return <TableContainer component={Paper}>
        <Table padding="none">
            <TableBody>
                <TableRow sx={{ height: 22, borderLeft: 4, borderLeftColor: "transparent" }}>
                    <TableCell sx={team1Sx}>{team1}</TableCell>
                    <TableCell align="center" sx={{ width: 24, border: 0 }}>{result1}</TableCell>
                    <TableCell align="center" rowSpan={2} sx={{ width: 40, border: 0 }}>
                        <IconButton
                            color="primary"
                            disabled={result.loading}
                            onClick={() => {
                                const simAgainRequestBody = {
                                    year: result.year
                                    , ratings: result.ratings
                                    , draw: result.draw
                                };
                                /* eslint-disable no-fallthrough */
                                switch (stage) {
                                    case "F":
                                    case "3":
                                        simAgainRequestBody.finals = structuredClone(result.finals);
                                        simAgainRequestBody.thirdPlacePlayOff = structuredClone(result.thirdPlacePlayOff);
                                    case "SF":
                                        simAgainRequestBody.semiFinals = structuredClone(result.semiFinals);
                                    case "QF":
                                        simAgainRequestBody.quarterFinals = structuredClone(result.quarterFinals);
                                    case "R16":
                                        simAgainRequestBody.roundOf16 = structuredClone(result.roundOf16);
                                    case "R32":
                                        simAgainRequestBody.roundOf32 = structuredClone(result.roundOf32);
                                        simAgainRequestBody.groupTables = structuredClone(result.groupTables);
                                    case "G":
                                        simAgainRequestBody.groupMatches = structuredClone(result.groupMatches);
                                        break;
                                    default:
                                }
                                /* eslint-enable no-fallthrough */
                                switch (stage) {
                                    case "F":
                                        delete simAgainRequestBody.finals.goal1;
                                        delete simAgainRequestBody.finals.goal2;
                                        delete simAgainRequestBody.finals.psoWinner;
                                        break;
                                    case "3":
                                        delete simAgainRequestBody.thirdPlacePlayOff.goal1;
                                        delete simAgainRequestBody.thirdPlacePlayOff.goal2;
                                        delete simAgainRequestBody.thirdPlacePlayOff.psoWinner;
                                        break;
                                    case "SF":
                                        delete simAgainRequestBody.semiFinals[matchIndex].goal1;
                                        delete simAgainRequestBody.semiFinals[matchIndex].goal2;
                                        delete simAgainRequestBody.semiFinals[matchIndex].psoWinner;
                                        break;
                                    case "QF":
                                        delete simAgainRequestBody.quarterFinals[matchIndex].goal1;
                                        delete simAgainRequestBody.quarterFinals[matchIndex].goal2;
                                        delete simAgainRequestBody.quarterFinals[matchIndex].psoWinner;
                                        break;
                                    case "R16":
                                        delete simAgainRequestBody.roundOf16[matchIndex].goal1;
                                        delete simAgainRequestBody.roundOf16[matchIndex].goal2;
                                        delete simAgainRequestBody.roundOf16[matchIndex].psoWinner;
                                        break;
                                    case "R32":
                                        delete simAgainRequestBody.roundOf32[matchIndex].goal1;
                                        delete simAgainRequestBody.roundOf32[matchIndex].goal2;
                                        delete simAgainRequestBody.roundOf32[matchIndex].psoWinner;
                                        break;
                                    case "G":
                                        delete simAgainRequestBody.groupMatches[groupIndex][matchIndex].goal1;
                                        delete simAgainRequestBody.groupMatches[groupIndex][matchIndex].goal2;
                                        delete simAgainRequestBody.groupMatches[groupIndex][matchIndex].psoWinner;
                                        break;
                                    default:
                                }
                                dispatch(simWorldCupResultAgainAsync(simAgainRequestBody));
                            }}
                        >
                            <Refresh />
                        </IconButton>
                    </TableCell>
                </TableRow>
                <TableRow sx={{ height: 22, borderLeft: 4, borderLeftColor: "transparent" }}>
                    <TableCell sx={team2Sx}>{team2}</TableCell>
                    <TableCell align="center" sx={{ width: 24, border: 0 }}>{result2}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </TableContainer>;
}

function getMatch(result, stage, groupIndex, matchIndex) {
    switch (stage) {
        case "G":
            if (!result.groupMatches || !result.groupMatches[groupIndex]) {
                return null;
            }
            return result.groupMatches[groupIndex][matchIndex];
        case "R32":
            if (!result.roundOf32) {
                return null;
            }
            return result.roundOf32[matchIndex];
        case "R16":
            if (!result.roundOf16) {
                return null;
            }
            return result.roundOf16[matchIndex];
        case "QF":
            if (!result.quarterFinals) {
                return null;
            }
            return result.quarterFinals[matchIndex];
        case "SF":
            if (!result.semiFinals) {
                return null;
            }
            return result.semiFinals[matchIndex];
        case "3":
            return result.thirdPlacePlayOff || null;
        case "F":
            return result.finals || null;
        default:
            return null;
    }
}